// ============================================================================
// COMPONENT: NodeRedUISection.jsx
// client/src/components/labEngine/NodeRedUISection.jsx
//
// Inline Node-RED dashboard UI widgets — rendered as a section inside
// NodeRedDashboard. Receives circuit already loaded by the parent page.
// Manages its own MQTT connection independently.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import mqtt from 'mqtt';

import './NodeRedUISection.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_CHART_POINTS = 60;

// ── SemiGauge ─────────────────────────────────────────────────────────────────

const SemiGauge = ({ value, min, max, label, unit, colorLow, colorMid, colorHigh, thresholdLow, thresholdMid }) => {
  const pct     = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const angle   = -180 + pct * 180;
  const cx = 100, cy = 100, r = 75;
  const color   = value >= thresholdMid ? (colorHigh || '#ef4444')
    : value >= thresholdLow ? (colorMid  || '#f59e0b')
    : (colorLow || '#10b981');
  const toRad   = deg => (deg * Math.PI) / 180;
  const needleX = cx + r * Math.cos(toRad(angle));
  const needleY = cy + r * Math.sin(toRad(angle));

  return (
    <div className="nrui-gauge-wrap">
      <svg viewBox="0 0 200 120" className="nrui-gauge-svg">
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
          fill="none" stroke="#e2e8f0" strokeWidth="12" strokeLinecap="round"/>
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${needleX} ${needleY}`}
          fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"/>
        <line x1={cx} y1={cy} x2={needleX} y2={needleY}
          stroke="#1e293b" strokeWidth="3" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="5" fill="#1e293b"/>
        <text x={cx} y={cy-10} textAnchor="middle" fontSize="22" fontWeight="700" fill={color}>
          {typeof value === 'number' ? value.toFixed(1) : value}
        </text>
        <text x={cx} y={cy+8} textAnchor="middle" fontSize="10" fill="#64748b">{unit}</text>
        <text x={cx-r+4} y={cy+16} fontSize="9" fill="#94a3b8">{min}</text>
        <text x={cx+r-4} y={cy+16} fontSize="9" fill="#94a3b8" textAnchor="end">{max}</text>
      </svg>
      <div className="nrui-gauge-label">{label}</div>
    </div>
  );
};

// ── Widget components ─────────────────────────────────────────────────────────

const ChartWidget = ({ config, data }) => {
  const field = config?.field?.replace('payload.', '') || 'value';
  const color = config?.color || '#3FADB5';
  return (
    <div className="nrui-widget nrui-widget--chart">
      <div className="nrui-widget-label">{config?.label || 'Chart'}</div>
      {data.length === 0 ? (
        <div className="nrui-no-data">No data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top:4, right:8, bottom:4, left:-20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="time" tick={{ fontSize:10, fill:'#94a3b8' }} tickLine={false}/>
            <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} tickLine={false} axisLine={false}
              domain={[config?.ymin || 'auto', config?.ymax || 'auto']}/>
            <Tooltip contentStyle={{ fontSize:12, border:'1px solid #e2e8f0', borderRadius:6 }}/>
            <Line type={config?.interpolate || 'linear'} dataKey={field}
              stroke={color} strokeWidth={2} dot={false} isAnimationActive={false}/>
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const GaugeWidget = ({ config, value }) => (
  <div className="nrui-widget nrui-widget--gauge">
    <SemiGauge
      value={typeof value === 'number' ? value : 0}
      min={config?.min ?? 0}
      max={config?.max ?? 100}
      label={config?.label || 'Gauge'}
      unit={config?.unit || ''}
      colorLow={config?.colorLow}
      colorMid={config?.colorMid}
      colorHigh={config?.colorHigh}
      thresholdLow={config?.thresholdLow ?? 30}
      thresholdMid={config?.thresholdMid ?? 70}
    />
  </div>
);

const TextWidget = ({ config, value }) => {
  const display = value !== null && value !== undefined
    ? `${config?.prefix || ''}${typeof value === 'number' ? value.toFixed(config?.decimals ?? 1) : value}${config?.suffix || ''}`
    : '—';
  return (
    <div className="nrui-widget nrui-widget--text">
      <div className="nrui-widget-label">{config?.label || 'Value'}</div>
      <div className="nrui-text-value"
        style={{ fontSize: config?.fontSize || 28, color: config?.color || '#1e293b' }}>
        {display}
      </div>
    </div>
  );
};

const ButtonWidget = ({ config, onPublish }) => {
  const [pressed, setPressed] = useState(false);
  const handleClick = () => {
    setPressed(true);
    setTimeout(() => setPressed(false), 200);
    let payload = config?.payload ?? 'true';
    if (config?.payloadType === 'num')  payload = Number(payload);
    if (config?.payloadType === 'json') { try { payload = JSON.parse(payload); } catch {} }
    if (config?.payloadType === 'date') payload = new Date().toISOString();
    onPublish(config?.topic || 'iiot/command', payload);
  };
  return (
    <div className="nrui-widget nrui-widget--button">
      <button
        className={`nrui-btn ${pressed ? 'nrui-btn--pressed' : ''}`}
        style={{ background: config?.color || '#3FADB5' }}
        onClick={handleClick}
      >
        {config?.icon && <span>{config.icon} </span>}
        {config?.label || 'Button'}
      </button>
    </div>
  );
};

const SliderWidget = ({ config, onPublish }) => {
  const [val, setVal] = useState(config?.min ?? 0);
  const handleChange  = (e) => {
    const v = Number(e.target.value);
    setVal(v);
    if (config?.outs === 'all') onPublish(config?.topic || 'iiot/setpoint', v);
  };
  const handleRelease = () => {
    if (config?.outs !== 'all') onPublish(config?.topic || 'iiot/setpoint', val);
  };
  return (
    <div className="nrui-widget nrui-widget--slider">
      <div className="nrui-widget-label">{config?.label || 'Slider'}</div>
      <div className="nrui-slider-row">
        <span className="nrui-slider-min">{config?.min ?? 0}</span>
        <input type="range" className="nrui-slider"
          min={config?.min ?? 0} max={config?.max ?? 100} step={config?.step ?? 1}
          value={val} onChange={handleChange}
          onMouseUp={handleRelease} onTouchEnd={handleRelease}
        />
        <span className="nrui-slider-max">{config?.max ?? 100}</span>
      </div>
      <div className="nrui-slider-value">{val}</div>
    </div>
  );
};

const SwitchWidget = ({ config, onPublish }) => {
  const [on, setOn] = useState(false);
  const handleToggle = () => {
    const next = !on;
    setOn(next);
    const payload = next
      ? (config?.payloadType === 'bool' ? true  : config?.onvalue  ?? true)
      : (config?.payloadType === 'bool' ? false : config?.offvalue ?? false);
    onPublish(config?.topic || 'iiot/switch', payload);
  };
  return (
    <div className="nrui-widget nrui-widget--switch">
      <div className="nrui-widget-label">{config?.label || 'Switch'}</div>
      <button
        className={`nrui-switch ${on ? 'nrui-switch--on' : ''}`}
        style={{ '--on-color': config?.oncolor || '#3FADB5' }}
        onClick={handleToggle}
        aria-pressed={on}
      >
        <span className="nrui-switch-knob"/>
      </button>
      <div className="nrui-switch-state">{on ? 'ON' : 'OFF'}</div>
    </div>
  );
};

const NotificationToast = ({ notifications }) => (
  <div className="nrui-toasts">
    {notifications.map((n, i) => (
      <div key={i} className={`nrui-toast nrui-toast--${n.type || 'info'}`}>
        <strong>{n.title}</strong>
        <span>{n.message}</span>
      </div>
    ))}
  </div>
);

// ── Module-level MQTT singleton — survives re-renders ─────────────────────
let _mqttClient = null;
let _mqttTopic  = null;

const getMqttClient = (protocol, broker, port, settings) => {
  const url = `${protocol}://${broker}:${port}/mqtt`;
  if (_mqttClient && _mqttTopic === url) return _mqttClient;
  
  if (_mqttClient) { _mqttClient.end(true); _mqttClient = null; }

  _mqttClient = mqtt.connect(url, {
    clientId:        settings.clientId  || `nrd-ui-${Date.now()}`,
    username:        settings.username  || undefined,
    password:        settings.password  || undefined,
    keepalive:       settings.keepAlive || 60,
    clean:           true,
    connectTimeout:  15000,
    reconnectPeriod: 0,
  });
  _mqttTopic = url;
  return _mqttClient;
};

// ── Main export ───────────────────────────────────────────────────────────────
const NodeRedUISection = ({ circuitId, gatewayComp, isEngineRunning, liveData }) => {

  const mqttClientRef = useRef(null);
  const hasConnected = useRef(false);

  const [mqttStatus,    setMqttStatus]    = useState('disconnected');
  const [chartData,     setChartData]     = useState({});
  const [gaugeValues,   setGaugeValues]   = useState({});
  const [textValues,    setTextValues]    = useState({});
  const [notifications, setNotifications] = useState([]);
  const [lastMessage,   setLastMessage]   = useState(null);
  const [messageCount,  setMessageCount]  = useState(0);

  // ── Stable derivations — only recompute when circuitId changes ────────────
  const flowNodes      = useMemo(() => gatewayComp?.nodeRedFlow?.nodes || [],                    [circuitId]);
  const dashboardNodes = useMemo(() => flowNodes.filter(n => n.category === 'nr_dashboard'),     [circuitId]);
  const outputNodes    = useMemo(() => flowNodes.filter(n => n.category === 'nr_output'),        [circuitId]);

  const mqttSettings   = useMemo(() => {
    const mqttOutNode = outputNodes.find(n => n.nodeRedType === 'mqtt out');
    return mqttOutNode?.communicationConfigs?.mqtt?.settings || {};
  }, [circuitId]);

  const mqttBroker     = useMemo(() => (mqttSettings.broker || 'broker.hivemq.com').split(',')[0].trim(), [circuitId]);
  const subscribeTopic = useMemo(() => mqttSettings.topic  || 'iiot/telemetry',                          [circuitId]);
  const mqttProtocol   = useMemo(() => mqttSettings.useSSL ? 'wss' : 'ws',                               [circuitId]);
  const mqttPort       = useMemo(() => parseInt(mqttSettings.wsPort) || (mqttSettings.useSSL ? 8884 : 8000), [circuitId]);

  // ── Message processing ─────────────────────────────────────────────────────
  const processMessage = useCallback((payload) => {
    let data;
    try { data = JSON.parse(payload); } catch { data = { value: payload }; }

    setLastMessage(data);
    setMessageCount(c => c + 1);
    const now = new Date().toLocaleTimeString();

    console.log('[processMessage] data:', data);

    dashboardNodes.forEach(node => {
      const cfg = node.communicationConfigs?.chart?.settings
        || node.communicationConfigs?.gauge?.settings
        || node.communicationConfigs?.text?.settings
        || node.communicationConfigs?.notification?.settings
        || {};
      const field = (cfg.field || 'payload').replace('payload.', '');
      const value = field === 'payload' ? data : (data[field] ?? null);

      if (node.nodeRedType === 'ui_chart') {
        setChartData(prev => {
          const pts = prev[node.instanceId] || [];
          return { ...prev, [node.instanceId]: [...pts, { time: now, [field]: value }].slice(-MAX_CHART_POINTS) };
        });
      }
      if (node.nodeRedType === 'ui_gauge') setGaugeValues(prev => ({ ...prev, [node.instanceId]: value }));
      if (node.nodeRedType === 'ui_text')  setTextValues(prev  => ({ ...prev, [node.instanceId]: value }));
      if (node.nodeRedType === 'ui_toast' && (data.alarm === true || data.alarm === 1)) {
        const toast = { type: 'error', title: cfg.title || 'Alert',
          message: `Alarm triggered at ${now}`, id: Date.now() };
        setNotifications(prev => [...prev.slice(-4), toast]);
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== toast.id)),
          (cfg.displayTime || 3) * 1000);
      }
    });
  }, [dashboardNodes]);

  // ── Message processing ─────────────────────────────────────────────────────
  const processMessageRef = useRef(processMessage);
  useEffect(() => { processMessageRef.current = processMessage; }, [processMessage]);

  // ── Feed widgets from liveData polling (replaces MQTT for now) ────────────
  useEffect(() => {
    if (!liveData?.sensorText) return;
    const now = new Date().toLocaleTimeString();

    // Build a flat payload from sensorText — same shape as MQTT message
    // sensorText: { "DHT22 4": 46.7, "MQ-2 Gas Sensor 5": 8899 }
    const sensorValues = Object.values(liveData.sensorText);
    const payload = {
      temperature: sensorValues[0] ?? null,
      gasLevel:    sensorValues[1] ?? null,
      ...liveData.sensorText,
    };

    setLastMessage(payload);
    setMessageCount(c => c + 1);

    dashboardNodes.forEach(node => {
      const cfg = node.communicationConfigs?.chart?.settings
        || node.communicationConfigs?.gauge?.settings
        || node.communicationConfigs?.text?.settings
        || node.communicationConfigs?.notification?.settings
        || {};
      
      console.log('[widget]', node.nodeRedType, 'field:', cfg.field, 'label:', cfg.label);
      
      const field = (cfg.field || 'payload').replace('payload.', '');
      const value = payload[field] ?? null;

      if (value === null) return;

      if (node.nodeRedType === 'ui_chart') {
        setChartData(prev => {
          const pts = prev[node.instanceId] || [];
          return { ...prev, [node.instanceId]: [...pts, { time: now, [field]: value }].slice(-MAX_CHART_POINTS) };
        });
      }
      if (node.nodeRedType === 'ui_gauge') setGaugeValues(prev => ({ ...prev, [node.instanceId]: value }));
      if (node.nodeRedType === 'ui_text')  setTextValues(prev  => ({ ...prev, [node.instanceId]: value }));
    });
  }, [liveData, dashboardNodes]);

  // ── MQTT connection — singleton, only fires on engine start/stop ──────────
  // NOTE: MQTT receives messages when HiveMQ bridges TCP→WebSocket correctly.
  // Currently widgets are fed via liveData polling instead.
  useEffect(() => {
    if (!circuitId || dashboardNodes.length === 0) return;

    if (isEngineRunning && !hasConnected.current) {
      hasConnected.current = true;
      setMqttStatus('connecting');

      const client = getMqttClient(mqttProtocol, mqttBroker, mqttPort, mqttSettings);
      mqttClientRef.current = client;
      client.removeAllListeners();

      client.on('connect', () => {
        setMqttStatus('connected');
        client.subscribe(subscribeTopic, { qos: 0 });
      });
      client.on('message', (_, msg) => {
        processMessageRef.current(msg.toString());
      });
      client.on('error',     () => setMqttStatus('error'));
      client.on('offline',   () => setMqttStatus('disconnected'));
      client.on('reconnect', () => setMqttStatus('connecting'));

    } else if (!isEngineRunning && hasConnected.current) {
      hasConnected.current = false;
      if (_mqttClient) {
        _mqttClient.end(true);
        _mqttClient = null;
        _mqttTopic  = null;
      }
      mqttClientRef.current = null;
      setMqttStatus('disconnected');
    }
  }, [isEngineRunning, circuitId]);

  // ── Publish ────────────────────────────────────────────────────────────────
  const handlePublish = useCallback((topic, payload) => {
    const client = mqttClientRef.current;
    if (!client?.connected) return;
    const msg = typeof payload === 'object' ? JSON.stringify(payload) : String(payload);
    client.publish(topic, msg, { qos: 0 });
  }, []);

  // ── Widget renderer ────────────────────────────────────────────────────────
  const renderWidget = (node) => {
    const cfg = node.communicationConfigs?.chart?.settings
      || node.communicationConfigs?.gauge?.settings
      || node.communicationConfigs?.text?.settings
      || node.communicationConfigs?.button?.settings
      || node.communicationConfigs?.slider?.settings
      || node.communicationConfigs?.switch?.settings
      || node.communicationConfigs?.notification?.settings
      || {};
    switch (node.nodeRedType) {
      case 'ui_chart':  return <ChartWidget  key={node.instanceId} config={cfg} data={chartData[node.instanceId] || []}/>;
      case 'ui_gauge':  return <GaugeWidget  key={node.instanceId} config={cfg} value={gaugeValues[node.instanceId] ?? 0}/>;
      case 'ui_text':   return <TextWidget   key={node.instanceId} config={cfg} value={textValues[node.instanceId] ?? null}/>;
      case 'ui_button': return <ButtonWidget key={node.instanceId} config={cfg} onPublish={handlePublish}/>;
      case 'ui_slider': return <SliderWidget key={node.instanceId} config={cfg} onPublish={handlePublish}/>;
      case 'ui_switch': return <SwitchWidget key={node.instanceId} config={cfg} onPublish={handlePublish}/>;
      default:          return null;
    }
  };

  const STATUS = {
    connected:    { label: '● Connected',    color: '#10b981' },
    connecting:   { label: '◌ Connecting…',  color: '#f59e0b' },
    disconnected: { label: '○ Disconnected', color: '#6b7280' },
    error:        { label: '✕ Error',        color: '#ef4444' },
  };

  // Hide section entirely if no dashboard nodes
  if (dashboardNodes.length === 0) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <section className="nrd-section">
      <div className="nrd-section-title-row">
        <h3 className="nrd-section-title">📊 Dashboard UI</h3>
        <div className="nrui-section-toolbar">
          <span className="nrui-topic-chip">📡 {subscribeTopic}</span>
          <span className="nrd-flow-label-tag">
            {messageCount} msg{messageCount !== 1 ? 's' : ''}
          </span>
          <span className="nrui-status-badge"
            style={{ background: STATUS[mqttStatus]?.color }}>
            {STATUS[mqttStatus]?.label}
          </span>
        </div>
      </div>

      {lastMessage && (
        <div className="nrui-last-msg">
          <span className="nrui-last-msg-label">Last:</span>
          <span className="nrui-last-msg-payload">
            {JSON.stringify(lastMessage).slice(0, 200)}
          </span>
        </div>
      )}

      <div className="nrui-grid">
        {dashboardNodes.map(renderWidget)}
      </div>

      <NotificationToast notifications={notifications}/>
    </section>
  );
};

export default NodeRedUISection;